import React from "react";

export function KeyMetricsHelp() {
    return (
        <>
            <p>
                When it comes to tracking performance of your podcast there are a lot of metrics to
                consider. The Key Metrics report focuses on the 5 key metrics that are most
                important to understanding your show’s growth at the top level. It is designed to
                give you a quick status report on how your podcast is doing right now. Check back
                often as these metrics are based on rolling data and will update every day.
            </p>

            <p>
                More detailed data on your show is available on the <b>Show Overview</b>.
            </p>
            <p>
                Here are the <strong>5 Key Metrics</strong> you’ll want to monitor to keep your
                finger on the pulse of your show’s growth:
            </p>

            <h1>All-Time Downloads</h1>
            <AllTimeDownloadsHelp />

            <h1>Average 7-Day Downloads per Episode</h1>
            <AverageDownloadsPerEpisodeHelp />

            <h1>30-Day Downloads</h1>
            <ThirtyDayDownloadsHelp />

            <h1>30-Day Audience</h1>
            <ThirtyDayAudienceHelp />

            <h1>Latest Episode - Downloads to Date</h1>
            <LatestEpisodeHelp />
        </>
    );
}

export function ShowDetailsHelp() {
    return (
        <>
            <p>
                While the Key Metrics report focuses on the 5 key metrics that are most important to
                understanding your show’s growth at the top level,there are times when you will want
                to dive further into the metrics behind your show. The Show Overview page is
                designed to give you deeper insight into a specific show’s performance enabling you
                to formulate action steps to improve that performance and grow your show.
            </p>

            <p>
                The Show Overview report has five main sections, each which drills down to more
                detailed reports. Here is information on what you’ll find in the five sections and
                along with the data points available in each and how to use them.
            </p>

            <h1>All-Time Downloads</h1>
            <AllTimeDownloadsHelp />

            <h1>30 Day Downloads and Audience</h1>

            <h2>30-Day Downloads - Global and US</h2>
            <ThirtyDayDownloadsHelp region="Global and US" />

            <h2>30-Day Audience - Global and US</h2>
            <ThirtyDayAudienceHelp region="Global and US" />

            <h1>Episode Data</h1>

            <h2>7-Day Global Episode Delivery</h2>
            <EpisodeGrowthHelp />

            <h2>30-Day Average Global and US Downloads per Episode</h2>
            <AverageDownloadsPerEpisodeHelp region="Global and US" />
        </>
    );
}

export function AllTimeDownloadsHelp() {
    return (
        <>
            <p>
                <strong>What it is:</strong> The total number of times your show has been downloaded
                (Global Downloads) across all of your show’s episodes from the point in time when
                you began measuring with Podtrac. Podtrac can’t see traffic to your show before the
                time you added the redirect. Downloads reported by Podtrac include both file
                downloads to a device as well as streaming delivery of podcast audio.
            </p>
            <p>
                <strong>Why it’s important:</strong> We would consider this the least important
                metric on this page - more of a vanity metric. We’ve included it here because it is
                your biggest number and it is always fun to see how your show has grown over time,
                but it doesn’t have a lot of meaning without understanding more details about the
                show, e.g. show frequency, number of episodes, how long the show has been
                publishing, etc.
            </p>
            <p>
                <strong>How to use it:</strong> We often hear podcasters cite All-time Downloads as
                in “we just hit 1 million downloads!” While it can be fun to share a large number
                with your audience and it feels good to see a number that is always growing, just
                keep in mind that on it’s own it isn’t really telling you anything about your show.
                As long as you have episodes available (whether you published new ones or not) this
                number will continue to grow.
            </p>
        </>
    );
}

export function AverageDownloadsPerEpisodeHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> The Average 7-Day Downloads per Episode is the average{" "}
                {region} downloads in the first 7 days after posting for the most recent 5 episodes
                with 7 days of data. Downloads reported by Podtrac include both file downloads to a
                device as well as streaming delivery of podcast audio.
            </p>
            <p>
                <strong>Why it’s important:</strong> For most podcasts, 80 to 90% of total episode
                downloads happen in the first 7 days.
            </p>
            <p>
                <strong>How to use it:</strong> Knowing your podcast’s average performance in the
                first 7 days after posting gives you a benchmark by which to judge your current
                episode's performance as well as an idea of the size of your show.
            </p>
            <p>
                While there are different time-frames over which you could look at average downloads
                per episode, we choose 7 days over 5 episodes for this report because it gives you
                an average of current episodes to use to gauge your most recent episode. This lets
                you focus on the growth of your current episode.
            </p>
            <p>
                If you are interested in projecting downloads for future episodes for purposes of
                selling ads or just understanding longer term delivery, we recommend looking at a
                30-Day download average.
            </p>
        </>
    );
}

export function ThirtyDayDownloadsHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> Total {region} Downloads across all of your show’s
                episodes during the last 30 days. This number is updated every night to reflect the
                most recent 30 days. The month/month percentage number compares this current 30 days
                to the previous 30 days and lets you know if you are trending upwards or downwards
                over total downloads. Downloads reported by Podtrac include both file downloads to a
                device as well as streaming delivery of podcast audio.
            </p>
            <p>
                <strong>Why it’s important:</strong> Downloads are the measure of how many times
                your show’s episodes are listened to. Your 30-Day Download number gives you an idea
                of the total monthly “inventory” for your show (if you are selling ads) and a good
                idea of how much traffic your show sees in a month.
            </p>
            <p>
                <strong>How to use it:</strong> Use the absolute 30-Day Download number to give you
                a sense for the amount of traffic your show does in a month. Use the % change value
                to monitor whether or not your show is growing over time and by how much. The bigger
                the % change the better.
            </p>
        </>
    );
}

export function ThirtyDayAudienceHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> Total number of {region} listeners to your show across
                all of your show’s episodes during the last 30 days. This number is updated every
                night to reflect the most recent 30 days. The month/month percentage number compares
                this current 30 days to the previous 30 days and lets you know if you are growing
                your show’s audience. Audience is also sometimes called Reach referring to how many
                people you are reaching with your show.
            </p>
            <p>
                <strong>Why it’s important:</strong> Audience is the measure of how many people are
                downloading your show. Your 30-Day Audience number gives you an idea of how many
                people you are reaching with your show each month.
            </p>
            <p>
                <strong>How to use it:</strong> Use the absolute 30-Day Audience number to give you
                a sense for the size of your monthly audience and to give you some insight into
                listener behaviour. For example, if Total 30-Day Downloads is growing but 30-Day
                Audience is flat or decreasing, that means your existing listeners are listening to
                more episodes. If, on the other hand, both are growing that means you are bringing
                in more new people to your show.
            </p>
            <p>
                Check the % change value often to monitor whether or not you are growing the number
                of people listening to your show.
            </p>
        </>
    );
}
export function EpisodeGrowthHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> For your most recent five episodes, the chart displays
                the number of {region} Downloads for each of the first 7 days (as available) since
                the episode was posted. The Growth vs avg. column on the chart compares the
                delivery-to-date to the average delivery for that same number of days for all
                episodes published in the prior 90 days.
            </p>
            <p>
                By rolling over the lines in the graph you can view both daily downloads and
                cumulative downloads for each episode.
            </p>
            <p>
                <strong>Why it’s important:</strong> For most podcasts, 80 to 90% of total episode
                downloads happen in the first 7 days.
            </p>

            <p>
                <strong>How to use it:</strong> Use this report to gain an understanding of how
                quickly listeners listen to your show. You can also check this report after posting
                a new episode to see how it is performing in its first few days versus the prior 4
                episodes.
            </p>
        </>
    );
}

export function LatestEpisodeHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> This section of the report lists the title and
                publication date of your most recent episode and displays the number of {region}{" "}
                Downloads the episode has received to-date (through the previous day). The number
                updates each night and the episode switches to a new episode the day after
                publication. Downloads reported by Podtrac include both file downloads to a device
                as well as streaming delivery of podcast audio.
            </p>
            <p>
                The “VS PREVIOUS” represents Current Episode Growth as compared to the prior
                episode. It is the percentage difference between the number of downloads to-date of
                your current episode versus the previous episode for the same number of days after
                publication.
            </p>
            <p>
                <strong>Why it’s important:</strong> When you publish an episode you want to know as
                quickly as possible if your show is growing or if there was a possible problem in
                posting or distribution of the episode. The Latest Episode - Downloads to Date lets
                you easily and quickly see (as early as the day after publication) how it is doing
                compared to your last episode.
            </p>
            <p>
                <strong>How to use it:</strong> If the Current Episode Growth number is positive you
                can rest assured that your episode is reaching your audience and your show is
                growing. If this number is negative by a lot you will want to check to make sure all
                your distribution channels are operating correctly. If so then you’ll want to think
                about why this particular episode is not resonating with your audience as well as
                your previous episode and then use that information to inform plans for your next
                episode.
            </p>
        </>
    );
}

export function TopCountriesHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> The Top Countries chart displays the top five countries
                for your podcast over the past 30 days and what percentage of your downloads are
                coming from each of those countries. The data is for a rolling 30 days and updates
                daily.
            </p>
            <p>
                <strong>Why it’s important and how to use it:</strong> Knowing where your listeners
                are based helps you to program your content to your listeners. The data can also be
                useful when selling ads or sponsorships in your show to help you and your sponsors
                understand the audience they are reaching.
            </p>
        </>
    );
}

export function TopAppsHelp({ region = "Global" }) {
    return (
        <>
            <p>
                <strong>What it is:</strong> The Top Apps chart displays the top five podcast
                listening sources for your podcast over the past 30 days and what percentage of your
                downloads are coming from each of those sources. We define a “source” as the app or
                software the listener is using to access your show. The data is for a
                rolling 30 days and updates daily.
            </p>
            <p>
                <strong>Why it’s important and how to use it:</strong> Understanding how your
                listeners access your show can be useful in understanding how to promote your show
                as well as in understanding factors which can affect the number of downloads your
                show gets (e.g. a software update in a platform which causes a change in listener
                behaviour).
            </p>
        </>
    );
}
